// The start-up and changeover walk-around.
//
// Sixty to seventy yes/no items in sections, walked around a line. This is the
// paper form, and it is deliberately NOT the keypad: a keypad is for one number
// you have just measured, and this is a list you work down while moving.
//
// THREE RULES THIS SCREEN IS BUILT AROUND, and they are the same three LineCheck
// has, for the same reasons.
//
// 1. THE ITEMS COME FROM THE RUN. GET /runs/{id}/readiness reads the obligations
//    created at run start, against the LINE spec version pinned then. Fetching
//    the live line spec instead would put a form somebody approved this morning
//    onto a walk-around that started under last month's.
//
// 2. THE ORDER IS THE SPEC'S — Sequence, which is global across the form. On a
//    start-up form the order is physical — you walk the line — so sorting by
//    anything else sends somebody back and forth past the same machine, and
//    sorting by SECTION NAME does exactly that (see bySection). ⚠ This is the one place that
//    deliberately differs from quality_readiness's own default ordering, which
//    is attention-first for the VERIFIER. Two audiences, two orders, and this
//    screen re-sorts rather than trusting the payload's order.
//
// 3. COMMIT ENQUEUES; IT DOES NOT POST. The queue stamps the capture id, the UPN
//    and the device time inside its own write. A changeover happens at the far
//    end of the line where the wifi is worst, and the whole point of the queue is
//    that the walk-around survives it.
//
// The verdict shown here is a PREVIEW — yes is green, no is red — and the server
// decides. It knows about competency, authority and the comment rule; the device
// does not, and cannot while it is offline.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { useQueue } from '../../offline/useQueue';
import { opFocus, opMessage } from '../../shells/OperatorShell';
import { LinePhotoPicker, ReferenceThumbs, SeenThumbs } from './LinePhotos';
import { evictReferencePhotos, listedReferences, photoTarget, type ReadingPhoto, type ReferencePhoto } from './linePhotoModel';
import { currentUpn } from '../../config/auth';
import type { DraftPhoto } from './receiveModel';
import {
  countInSpec,
  dropRefused,
  expectedText,
  isAnswered,
  needsEvidence,
  parseCount,
  type LocalAnswer,
} from './walkaroundModel';

export interface ReadinessItem {
  obligationId: number;
  status: 'OWED' | 'AWAITING_RESULT' | 'AWAITING_VERIFY' | 'MET' | 'MISSED' | 'WAIVED';
  blocksProduction: boolean;
  attention: string[];
  parameter: {
    id: number;
    code: string;
    name: string;
    unit: string | null;
    section: string | null;
    sequence: number;
    valueKind: 'NUMERIC' | 'ATTRIBUTE';
    controlPointType: 'NONE' | 'CCP' | 'OPRP';
    requiresEvidenceOnFail: boolean;
    /** Evidence on EVERY answer, not only a fail. Optional: an older API omits it. */
    requiresEvidence?: boolean;
    /** The spec's limits, for a NUMERIC item (a count). Read-only. */
    nominal?: number | null;
    min?: number | null;
    max?: number | null;
    /** "What good looks like" (migration 034). Absent = not known (rule 15), [] = none. */
    referencePhotos?: ReferencePhoto[] | null;
  };
  reading: {
    id: number | string;
    value: number | null;
    answer: string | number | null;
    verdict: 'IN' | 'ACTION' | 'OUT' | null;
    capturedByUpn: string | null;
    capturedAt: string | null;
    evidenceRef: string | null;
    /** Photos the server has of what was seen. Absent = not known (rule 15), [] = none. */
    photos?: ReadingPhoto[] | null;
  } | null;
  verification: { outcome: 'PASS' | 'FAIL'; verifiedByUpn: string | null; isPilot?: boolean } | null;
}

export interface ReadinessPayload {
  runId: number;
  /** A pilot run (rule 27): every answer and verification on it is a pilot record. */
  isPilot?: boolean;
  pilot?: { isPilot: boolean; acceptsNewEntries: boolean };
  hasForm: boolean;
  released: boolean;
  counts: {
    total: number;
    outstanding: number;
    owed: number;
    awaitingVerify: number;
    met: number;
    waived: number;
  };
  run: {
    id: number;
    status: 'OPEN' | 'CLOSED';
    line: { id: number; code: string; name: string };
  } | null;
  items: ReadinessItem[];
}

interface Props {
  runId: number;
  /** Refetched by the parent when the walk-around completes, so the parent can
   *  switch to the keypad without a full reload. */
  onChanged?: () => void;
}

const SECTION_UNGROUPED = 'Checks';

/** A DOM id for a section heading. Section names carry spaces, slashes and
 *  ampersands ("Receival / Flour Milling Section"), none of which belong in an
 *  id that scrollIntoView and aria-controls both have to resolve. */
function sectionDomId(name: string): string {
  return 'sec-' + name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

/**
 * Group into sections, in the order the FORM puts them.
 *
 * ⚠ SORT BY SEQUENCE ALONE. An earlier version sorted by section name first and
 * it was wrong in a way that only showed up on screen: `Sequence` is global
 * across the whole form, so ordering by name put "Extruder Section" before
 * "Receival / Flour Milling Section" — alphabetical, and the exact opposite of
 * the physical order somebody walks. On the Bhuja Fryer form that sends the
 * operator to the middle of the line, back to the start, then to the fryer.
 *
 * A Map keeps insertion order, so grouping after a sequence sort yields sections
 * in the order their first item appears — which is the order they are printed,
 * which is the order you walk.
 */
function bySection(items: ReadinessItem[]): Array<[string, ReadinessItem[]]> {
  const groups = new Map<string, ReadinessItem[]>();
  for (const it of [...items].sort(
    (a, b) => a.parameter.sequence - b.parameter.sequence || a.parameter.id - b.parameter.id,
  )) {
    const key = it.parameter.section ?? SECTION_UNGROUPED;
    const list = groups.get(key);
    if (list) list.push(it);
    else groups.set(key, [it]);
  }
  return [...groups.entries()];
}

export function Walkaround({ runId, onChanged }: Props) {
  const { enqueue, refusals, addPhoto, discardPhoto } = useQueue();
  /** Who is signed in now: a photo is offered only on their own answer (photoTarget). */
  const me = currentUpn();
  const [data, setData] = useState<ReadinessPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Answers keyed while offline. The screen shows these immediately; the server
  // is the one that decides, and a refresh replaces them with what it accepted.
  const [answers, setAnswers] = useState<Record<number, LocalAnswer>>({});
  const [comments, setComments] = useState<Record<number, string>>({});
  /** What has been typed into a NUMERIC item's count box, not yet recorded. */
  const [countText, setCountText] = useState<Record<number, string>>({});
  /** Photos taken on this device after a bad answer, per item. Each is already in
   *  the queue (`photoKey`); the list is only what the picker shows. */
  const [photos, setPhotos] = useState<Record<number, Array<DraftPhoto & { photoKey: string }>>>({});

  /** The captures the server REFUSED. A refused attempt is not an answer (rule 4,
   *  rule 15): it is dropped from `answers`, so the item reads as owed again. */
  const refusedIds = useMemo(
    () => new Set(refusals.map((r) => r.clientCaptureId)),
    [refusals],
  );
  useEffect(() => {
    setAnswers((a) => dropRefused(a, refusedIds));
  }, [refusedIds]);
  const [busy, setBusy] = useState<number | null>(null);

  /**
   * Items where the operator has pressed "No" but not yet supplied the comment
   * the form demands.
   *
   * ⚠ THIS EXISTS TO BREAK A DEADLOCK, and the deadlock is worth remembering.
   * Revealing the comment box only once an item is answered "No" seems obvious —
   * until you notice that answering "No" is refused while the comment is empty.
   * The box would then never appear, and the item could never be failed. So the
   * PRESS records an intent, the box appears, and the second press commits.
   */
  const [noIntent, setNoIntent] = useState<Set<number>>(new Set());

  /* ── Section navigation ────────────────────────────────
   * A start-up form is 32 items on the Bhuja Fryer and 75 on Packing, worked
   * standing up, one-handed, while walking. Without this the only way to reach
   * the fryer section is to scroll past everything before it — twice, because
   * you come back to it when something needs re-checking.
   *
   * The strip is the form's own sections in the form's own order, so it doubles
   * as a map of the walk: where you are, what is behind you, what is left.
   */
  const scrollBox = useRef<HTMLDivElement | null>(null);
  const [activeSection, setActiveSection] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    api
      .get(`/runs/${runId}/readiness`)
      .then((d: unknown) => {
        const payload = d as ReadinessPayload;
        setData(payload);
        // The bytes cached for photos this form no longer lists go (a retired photo, a new version).
        const listed = listedReferences((payload.items ?? []).map((it) => it.parameter.referencePhotos));
        if (listed) void evictReferencePhotos('readiness', listed);
      })
      .catch((e: unknown) => setError(opMessage(e)));
  }, [runId]);

  useEffect(load, [load]);

  const sections = useMemo(() => bySection(data?.items ?? []), [data]);

  /** What the operator has answered: what the server ACCEPTED, plus what is
   *  sitting in the queue and has not been refused. */
  const answered = useMemo(() => {
    if (!data) return 0;
    return data.items.filter((it) =>
      isAnswered(it.reading !== null, answers[it.obligationId], refusedIds),
    ).length;
  }, [data, answers, refusedIds]);

  /** Answered / total per section, for the strip. Counts a queued answer as
   *  answered — the operator has done the work whether or not the wifi has. */
  const sectionProgress = useMemo(() => {
    const out = new Map<string, { done: number; total: number }>();
    for (const [name, items] of sections) {
      out.set(name, {
        done: items.filter((it) =>
          isAnswered(it.reading !== null, answers[it.obligationId], refusedIds),
        ).length,
        total: items.length,
      });
    }
    return out;
  }, [sections, answers, refusedIds]);

  /** Which section is under the operator's thumb. Observed rather than computed
   *  from scroll maths, so it stays right when a card grows because a comment
   *  box opened. `rootMargin` biases the trigger line to the upper third: the
   *  heading you are reading is the one that should be lit, not the one that has
   *  only just entered from the bottom. */
  useEffect(() => {
    const root = scrollBox.current;
    if (!root || sections.length === 0) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActiveSection(visible.target.getAttribute('data-section'));
      },
      { root, rootMargin: '0px 0px -66% 0px', threshold: 0 },
    );
    for (const [name] of sections) {
      const el = root.querySelector(`[data-section="${CSS.escape(name)}"]`);
      if (el) obs.observe(el);
    }
    return () => obs.disconnect();
  }, [sections]);

  const jumpTo = useCallback((name: string) => {
    const root = scrollBox.current;
    const el = root?.querySelector(`[data-section="${CSS.escape(name)}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSection(name);
  }, []);

  const commit = useCallback(
    async (item: ReadinessItem, value: number) => {
      const comment = (comments[item.obligationId] ?? '').trim();

      // The one rule worth catching before the queue: an answer the server will
      // refuse for want of evidence, and on a walk-around that refusal arrives
      // minutes later when the queue drains — long after the operator has walked
      // away from the thing they were looking at.
      if (needsEvidence(item.parameter, value) && comment === '') {
        if (item.parameter.requiresEvidence) {
          // Evidence on EVERY answer: the box is already showing, so say why.
          setError(
            `"${item.parameter.name}" needs a short evidence note every time it is recorded: the spec asks for evidence on this check, whatever the answer. Write what you checked, then press ${
              item.parameter.valueKind === 'NUMERIC' ? 'Record' : 'your answer'
            } again.`,
          );
          return;
        }
        // First press: open the box and say why, rather than refusing flatly.
        setNoIntent((prev) => new Set(prev).add(item.obligationId));
        setError(
          `"${item.parameter.name}" needs a comment saying what is wrong. Write one, then press No again.`,
        );
        return;
      }

      setBusy(item.obligationId);
      setError(null);
      try {
        const row = await enqueue({
          runId,
          specParameterId: item.parameter.id,
          meterId: null,
          // ATTRIBUTE: 1 = yes/pass, 0 = no/fail. The encoding is the server's;
          // the operator sees words and never a number. NUMERIC: the count typed.
          values: [value],
          evidenceRef: comment === '' ? null : comment,
          context: {
            lineCode: data?.run?.line.code ?? undefined,
            parameterCode: item.parameter.code,
            parameterName: item.parameter.name,
            runLabel: `run ${runId} start-up`,
          },
        });
        setAnswers((a) => ({
          ...a,
          [item.obligationId]: { value, clientCaptureId: row.reading.clientCaptureId },
        }));
        onChanged?.();
      } catch (e: unknown) {
        setError(opMessage(e));
      } finally {
        setBusy(null);
      }
    },
    [comments, enqueue, runId, data, onChanged],
  );

  /** A photo after a bad answer: to the queued answer while it may still be on the
   *  device, else to the reading the server has. Optional; never blocks anything. */
  const takePhotos = useCallback(
    async (item: ReadinessItem, added: Array<Pick<DraftPhoto, 'key' | 'blob' | 'capturedAtDevice'>>) => {
      const target = photoTarget(answers[item.obligationId]?.clientCaptureId, item.reading, currentUpn());
      if (target === null) return;
      try {
        for (const p of added) {
          const row = await addPhoto(target, { blob: p.blob, capturedAtDevice: p.capturedAtDevice }, {
            lineCode: data?.run?.line.code ?? undefined,
            parameterCode: item.parameter.code,
            parameterName: item.parameter.name,
            runLabel: `run ${runId} start-up`,
          });
          setPhotos((m) => ({
            ...m,
            [item.obligationId]: [
              ...(m[item.obligationId] ?? []),
              { ...p, rowKey: null, checkCode: null, photoKey: row.photoKey },
            ],
          }));
        }
      } catch (e: unknown) {
        setError(opMessage(e));
      }
    },
    [answers, addPhoto, data, runId],
  );

  const removePhoto = useCallback(
    async (item: ReadinessItem, key: string) => {
      const p = (photos[item.obligationId] ?? []).find((x) => x.key === key);
      if (!p) return;
      try {
        await discardPhoto(p.photoKey);
        setPhotos((m) => ({ ...m, [item.obligationId]: (m[item.obligationId] ?? []).filter((x) => x.key !== key) }));
      } catch {
        setError('That photo has already been sent, so it stays with the check.');
      }
    },
    [photos, discardPhoto],
  );

  if (error && !data) {
    return (
      <div className="p-(--s5)">
        <p role="alert" className="m-0 text-(length:--fs-lg) font-bold text-(--warn)">
          {error}
        </p>
        <button
          type="button"
          onClick={load}
          className={`mt-(--s4) min-h-[56px] rounded-(--radius) border border-(--border-strong) bg-(--card) px-(--s4) text-(length:--fs-md) font-semibold ${opFocus}`}
        >
          Try again
        </button>
      </div>
    );
  }

  if (!data) {
    return <p role="status" className="p-(--s5) text-(length:--fs-md)">Loading start-up checks…</p>;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {/* Compact on a phone (27 Oct 2026: this block, the tabs and the footer left 48px for the
          checks on an 812px screen): one heading line, the count beside it, the why from sm up. */}
      {/* ⚠ AND ON THE DOCKED IPAD (UI/UX audit, 31 Oct 2026, UX-41): at 1024x768 the scroll box
          had 278px of 768 for 5361px of checks. From sm up the heading and the count are held on
          one line, the two explanations (this one and the verifier hint that sat under the
          footer) are one muted line, and the padding is a row's, not a page's. The 56px
          targets below are untouched. */}
      <div className="flex-none border-b border-(--border) px-(--s4) py-(--s3) sm:px-(--s5) sm:py-(--s3)">
        <div className="flex flex-wrap items-baseline gap-x-(--s3) gap-y-(--s1) sm:flex-nowrap">
          <h1 className="m-0 min-w-0 text-(length:--fs-lg) font-bold sm:truncate sm:text-(length:--fs-xl)">
            Start-up checks{data.run ? ` — ${data.run.line.name}` : ''}
          </h1>
          <p className="m-0 text-(length:--fs-sm) text-(--muted) sm:shrink-0 sm:whitespace-nowrap sm:text-(length:--fs-md)">
            {answered} of {data.counts.total} answered · {data.counts.met} verified
            {data.counts.awaitingVerify > 0
              ? ` · ${data.counts.awaitingVerify} waiting on a signature`
              : ''}
          </p>
        </div>
        {/* The whole reason this screen blocks the keypad, said once, plainly - with who verifies. */}
        <p className="m-0 mt-(--s1) hidden text-(length:--fs-sm) text-(--muted) sm:block">
          Product checks stay locked until every check here is answered and a shift leader has
          verified it. A shift leader verifies from their own device, and cannot verify checks
          they captured.
        </p>
      </div>

      {/* The walk, as a strip. Horizontally scrollable rather than wrapped: a
          wrapped strip on Packing's seven sections would eat a third of the
          screen, and the screen's job is the checks. */}
      {sections.length > 1 ? (
        <nav
          aria-label="Sections"
          className="flex flex-none gap-(--s2) overflow-x-auto border-b border-(--border) bg-(--card) px-(--s4) py-(--s2)"
        >
          {sections.map(([name]) => {
            const p = sectionProgress.get(name) ?? { done: 0, total: 0 };
            const complete = p.total > 0 && p.done === p.total;
            const active = activeSection === name;
            return (
              <button
                key={name}
                type="button"
                onClick={() => jumpTo(name)}
                aria-current={active ? 'true' : undefined}
                className={`min-h-[56px] shrink-0 whitespace-nowrap rounded-(--radius) border px-(--s3) text-(length:--fs-sm) font-semibold ${opFocus} ${
                  active
                    ? 'border-(--primary) bg-(--primary-soft)'
                    : complete
                      ? 'border-(--ok-line) bg-(--ok-bg) text-(--ok)'
                      : 'border-(--border-strong) bg-(--card)'
                }`}
              >
                {name}
                <span className="ml-(--s2) font-mono">
                  {p.done}/{p.total}
                </span>
              </button>
            );
          })}
        </nav>
      ) : null}

      {error ? (
        <div
          role="alert"
          className="m-(--s4) rounded-(--radius) border border-(--warn-line) bg-(--warn-bg) p-(--s4) text-(length:--fs-md) font-semibold text-(--warn)"
        >
          {error}
        </div>
      ) : null}

      <div ref={scrollBox} className="min-h-0 flex-1 overflow-y-auto p-(--s4)">
        {sections.map(([section, items]) => (
          <section key={section} className="mb-(--s5)">
            {/* Sticky only without the strip (UI/UX audit, 31 Oct 2026, UX-41): with the strip
                showing, a pinned heading said the same section a second time and took a row of
                the 278px the iPad had left. It stays as the section's heading, and as the
                observer's and the strip's jump target. */}
            <h2
              id={sectionDomId(section)}
              data-section={section}
              className={`m-0 scroll-mt-(--s2) py-(--s2) text-(length:--fs-lg) font-bold ${
                sections.length > 1 ? '' : 'sticky top-0 z-10 bg-(--bg)'
              }`}
            >
              {section}
              <span className="ml-(--s3) font-mono text-(length:--fs-sm) font-normal text-(--muted)">
                {sectionProgress.get(section)?.done ?? 0}/{sectionProgress.get(section)?.total ?? 0}
              </span>
            </h2>
            <ul className="m-0 list-none p-0">
              {items.map((it) => {
                const local = answers[it.obligationId]?.value;
                const numeric = it.parameter.valueKind === 'NUMERIC';
                const server =
                  it.reading === null
                    ? null
                    : numeric
                      ? it.reading.value
                      : it.reading.verdict === 'IN'
                        ? 1
                        : 0;
                const value = local ?? server;
                const done = it.status === 'MET' || it.status === 'WAIVED';
                const expected = numeric ? expectedText(it.parameter) : null;
                // A photo is offered only on a bad answer: No, or a count out of spec. Never on Yes.
                const badAnswer =
                  !done && value !== null && (numeric ? !countInSpec(it.parameter, value) : value === 0);
                // ...and only where the photo can be sent: this device's answer, or the signed-in
                // person's own reading. A colleague's No offers no picker (not_your_reading).
                const canAddPhoto =
                  badAnswer && photoTarget(answers[it.obligationId]?.clientCaptureId, it.reading, me) !== null;
                const sentPhotos = it.reading?.photos ?? [];
                const showEvidence =
                  !done &&
                  (it.parameter.requiresEvidence === true ||
                    (it.parameter.requiresEvidenceOnFail &&
                      !numeric &&
                      (value === 0 || noIntent.has(it.obligationId))));
                return (
                  <li
                    key={it.obligationId}
                    className="mt-(--s3) rounded-(--radius) border border-(--border) bg-(--card) p-(--s4)"
                  >
                    <div className="flex items-center justify-between gap-(--s3)">
                      <ReferenceThumbs
                        specParameterId={it.parameter.id}
                        photos={it.parameter.referencePhotos}
                        source="readiness"
                        label={it.parameter.name}
                      />
                      <span className="min-w-0 flex-1 text-(length:--fs-md) font-semibold">
                        {it.parameter.name}
                        {it.parameter.controlPointType !== 'NONE' ? (
                          <span className="ml-(--s2) rounded-(--radius) bg-(--primary-soft) px-(--s2) font-mono text-(length:--fs-sm)">
                            {it.parameter.controlPointType}
                          </span>
                        ) : null}
                      </span>
                      <span className="shrink-0 font-mono text-(length:--fs-sm) text-(--muted)">
                        {it.status === 'AWAITING_VERIFY' ? 'awaiting verify' : ''}
                        {it.status === 'MET' ? 'verified' : ''}
                        {it.status === 'WAIVED' ? 'waived' : ''}
                      </span>
                    </div>

                    {numeric ? (
                      <div className="mt-(--s3)">
                        {expected ? (
                          <p className="m-0 text-(length:--fs-md) text-(--muted)">
                            {expected}. Count them and enter the number you counted.
                          </p>
                        ) : null}
                        <div className="mt-(--s2) flex gap-(--s3)">
                          <label className="flex-1">
                            <span className="sr-only">Count for {it.parameter.name}</span>
                            <input
                              type="text"
                              inputMode="decimal"
                              autoComplete="off"
                              disabled={done || busy === it.obligationId}
                              value={countText[it.obligationId] ?? (value === null ? '' : String(value))}
                              onChange={(e) =>
                                setCountText((c) => ({ ...c, [it.obligationId]: e.target.value }))
                              }
                              placeholder="Count"
                              className={`block min-h-[56px] w-full rounded-(--radius) border border-(--border-strong) bg-(--bg-input) px-(--s3) font-mono text-(length:--fs-lg) font-bold disabled:opacity-50 ${opFocus}`}
                            />
                          </label>
                          <button
                            type="button"
                            disabled={done || busy === it.obligationId}
                            onClick={() => {
                              const typed =
                                countText[it.obligationId] ?? (value === null ? '' : String(value));
                              const n = parseCount(typed);
                              if (n === null) {
                                setError(`Enter the count for "${it.parameter.name}" as a number.`);
                                return;
                              }
                              void commit(it, n);
                            }}
                            className={`min-h-[56px] flex-1 rounded-(--radius) border border-(--border-strong) bg-(--card) text-(length:--fs-lg) font-bold disabled:opacity-50 ${opFocus}`}
                          >
                            Record
                          </button>
                        </div>
                        {value !== null ? (
                          <p
                            className={`m-0 mt-(--s2) text-(length:--fs-md) font-semibold ${
                              countInSpec(it.parameter, value)
                                ? 'text-(--ok)'
                                : 'text-(--bad)'
                            }`}
                          >
                            Recorded: {value}
                            {it.parameter.unit ? ` ${it.parameter.unit}` : ''}
                          </p>
                        ) : null}
                      </div>
                    ) : (
                    <div className="mt-(--s3) flex gap-(--s3)">
                      <button
                        type="button"
                        disabled={done || busy === it.obligationId}
                        aria-pressed={value === 1}
                        onClick={() => commit(it, 1)}
                        className={`min-h-[56px] flex-1 rounded-(--radius) border text-(length:--fs-lg) font-bold disabled:opacity-50 ${opFocus} ${
                          value === 1
                            ? 'border-(--ok-line) bg-(--ok-bg) text-(--ok)'
                            : 'border-(--border-strong) bg-(--card)'
                        }`}
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        disabled={done || busy === it.obligationId}
                        aria-pressed={value === 0}
                        onClick={() => commit(it, 0)}
                        className={`min-h-[56px] flex-1 rounded-(--radius) border text-(length:--fs-lg) font-bold disabled:opacity-50 ${opFocus} ${
                          value === 0
                            ? 'border-(--bad-line) bg-(--bad-bg) text-(--bad)'
                            : 'border-(--border-strong) bg-(--card)'
                        }`}
                      >
                        No
                      </button>
                    </div>
                    )}

                    {/* ⚠ ONLY ONCE "No" IS CHOSEN. An earlier version showed this
                        on every item, reasoning that a field appearing under the
                        thumb would move the buttons. It does not — the field is
                        BELOW them — and showing it always nearly doubled the
                        height of every card. On a 32-item form that is an extra
                        two metres of scrolling, and on Packing's 75 it is five.
                        A walk-around is done standing up, moving, one-handed. */}
                    {/* requiresEvidence is the exception: evidence on EVERY answer,
                        so the box is always there and says so. */}
                    {showEvidence ? (
                      <label className="mt-(--s3) block text-(length:--fs-sm) text-(--muted)">
                        {it.parameter.requiresEvidence
                          ? 'Evidence note (required for every answer on this check)'
                          : 'Comment (required)'}
                        <input
                          value={comments[it.obligationId] ?? it.reading?.evidenceRef ?? ''}
                          onChange={(e) =>
                            setComments((c) => ({ ...c, [it.obligationId]: e.target.value }))
                          }
                          className="mt-(--s1) block min-h-[48px] w-full rounded-(--radius) border border-(--border-strong) bg-(--bg-input) px-(--s3) text-(length:--fs-md)"
                        />
                      </label>
                    ) : null}

                    {canAddPhoto ? (
                      <LinePhotoPicker
                        label={it.parameter.name}
                        photos={photos[it.obligationId] ?? []}
                        alreadySent={sentPhotos.length}
                        onAdd={(added) => void takePhotos(it, added)}
                        onRemove={(key) => void removePhoto(it, key)}
                      />
                    ) : null}

                    {it.reading && sentPhotos.length > 0 ? (
                      <div className="mt-(--s2) flex flex-wrap items-center gap-(--s2)">
                        <span className="text-(length:--fs-sm) text-(--muted)">
                          {sentPhotos.length === 1 ? '1 photo sent' : `${sentPhotos.length} photos sent`}
                        </span>
                        <SeenThumbs readingId={it.reading.id} photos={sentPhotos} label={it.parameter.name} />
                      </div>
                    ) : null}

                    {it.reading?.evidenceRef && done ? (
                      <p className="m-0 mt-(--s2) text-(length:--fs-sm) text-(--muted)">
                        {it.reading.evidenceRef}
                      </p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}

        {data.counts.total === 0 ? (
          <p className="text-(length:--fs-md) text-(--muted)">
            This line has no approved start-up form, so there is nothing to check here. That is not
            the same as passing — nothing was asked.
          </p>
        ) : null}
      </div>

      <div className="flex-none border-t border-(--border) px-(--s4) py-(--s2) sm:px-(--s4) sm:py-(--s2)">
        <div className="flex gap-(--s3)">
          <button
            type="button"
            onClick={load}
            className={`min-h-[56px] flex-1 rounded-(--radius) border border-(--border-strong) bg-(--card) px-(--s4) text-(length:--fs-md) font-semibold ${opFocus}`}
          >
            Refresh
          </button>
          {/* Appears only once every item has an answer, because a verifier
              called over to a half-finished walk-around is a verifier who stops
              coming when called. */}
          {data.counts.awaitingVerify > 0 && data.counts.owed === 0 ? (
            <Link
              to={`/op/run/${runId}/verify`}
              className={`flex min-h-[56px] flex-1 items-center justify-center rounded-(--radius) border border-(--border-strong) bg-(--primary) px-(--s4) text-(length:--fs-md) font-bold text-(--primary-fg) ${opFocus}`}
            >
              Ready to verify ({data.counts.awaitingVerify})
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default Walkaround;
